<template>
	<div class="content-page content-page--global-transaction">
		<div class="content-page__heading" ref="contentPageHeading">
			<div
				class="content-page__breadcrumb-title"
				ref="contentPageBreadcrumb"
			>
				<h2 class="content-page__title">Global Transaction Viewer</h2>
			</div>
			<div class="content-page__actions" ref="transactionPageAction">
				<Button
					className="btn--has-icon btn--small"
					:btnClick="exportAll"
					labelText="Export"
					iconClassName="icon-export"
					:iconBeforeLabel="true"
					:disabled="isDisabledExportButton"
				/>
			</div>
		</div>

		<div
			class="results-toolbar results-toolbar--variant-seven"
			ref="resultsToolBar"
		>
			<Search
				class="results-toolbar__search"
				variant="toggle-icons"
				:placeholder="'Search by any transaction information'"
				v-model="filters.search"
				:width="'300px'"
			/>
			<IconInfo
				:timeout="3000"
				class="results-toolbar__icon"
				:text="
					'Search by integration name, OEM name, vendor name/alias, transaction ID, or correlation ID.'
				"
				:position="position"
				:fontSize="'16px'"
				ref="iconInfo"
			/>
			<div
				class="results-toolbar__filter results-toolbar--global-transaction__desktop"
			>
				<div class="results-toolbar__filter-buttons">
					<Button
						className="btn--link"
						labelText="Restore Filters"
						:btnClick="resetFilters"
						v-show="showRestoreFiltersButton"
						data-test="btnRestoreFilters"
					/>
					<Button
						className="btn--has-icon"
						:labelText="advancedFilterLabel"
						:btnClick="handledAdvancedFilters"
						iconClassName="icon-filter"
						:iconBeforeLabel="true"
					/>
				</div>

				<DropdownV2
					dropdownWidth="140px"
					:options="transactionTypeOptions"
					:displayError="false"
					:displayHelp="false"
					:showClearSelectedButton="false"
					v-model="filters.logEntrytransactionTypes"
					ref="transactionTypeRef"
				/>
				<DropdownMultiselectV2
					:options="ddCustomerOptions()"
					:timeout="2000"
					placeholder="All Clients"
					v-model="filters.customers"
					ref="customersRef"
				/>
				<DropdownMultiselectV2
					:options="healthOptions"
					:timeout="5000"
					placeholder="All Health"
					v-model="filters.healths"
					ref="healthRef"
				/>
			</div>
			<div
				class="results-toolbar__filter results-toolbar--global-transaction__mobile"
			>
				<Button
					className="btn--primary"
					iconClassName="icon-filter"
					:iconBeforeLabel="true"
					:btnClick="handledAdvancedFilters"
				/>
			</div>
			<div class="results-toolbar__counter">
				<ResultCounter :counter="resultCounter" v-if="hasResults" />
			</div>
		</div>
		<GlobalTransactionsAdvancedFilters
			v-if="showAdvancedFiltersModal"
			@close="showAdvancedFiltersModal = false"
			:filters="filters"
			@filtersChanged="setFilters"
		/>
		<Spinner :processor="{ loading: loading || exportingAllTransaction }" />
		<NoResults v-if="!loading && !hasResults" />
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:class="{ 'is-disabled': exportingAllTransaction }"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<GlobalTransactionsList
				:items="globalTransactionsList"
				ref="globalTransactionsList"
			/>
		</ScrollContainer>
		<Modals
			v-model="showModal"
			type="confirmation"
			:title="
				`It's taking more than 60 seconds to load. Would you like to
				continue filter for another 60 seconds?`
			"
			:confirmationContent="''"
			labelConfirmButton="Yes, continue"
			labelCancelButton="Cancel"
			@confirm="reloadPage"
		/>
	</div>
</template>
<script>
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import DropdownMultiselectV2 from '@/components/forms/dropdown/dropdown-multiselect-v2.vue';
import GlobalTransactionsList from '@/sections/transactions/shared/transactions-list';
import Search from '@/components/forms/search/search';
import ResultCounter from '@/site-components/result-counter/result-counter';
import GlobalTransactionsAdvancedFilters from '@/sections/transactions/summary/global-transactions-advanced-filters.vue';
import CustomerOptions from '@/mixins/customers/customer-options';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import NoResults from '@/site-components/no-results/no-results';
import Modals from '@/components/shells/modal/modals';
import { mapActions, mapState } from 'vuex';
import IconInfo from '@/components/notifications/icon-info/icon-info';
import Spinner from '@/components/visual/spinner/spinner';
import { countsDiffObjects } from '@/utils/function';
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';
import { parseDateToUTC } from '@/utils/date/parseDate';
import _ from 'lodash';
// functions
import {
	removeObjectEmptyValues,
	removePropertiesWithSameValues,
	downloadCSV
} from '@/utils/object';

//const modules
import {
	MODULE_NAME,
	GLOBAL_TRANSACTIONS,
	SET_GLOBAL_TRANSACTIONS,
	RELOAD_TRANSACTIONS
} from '@/sections/transactions/transactions-mutation-types.js';
import {
	SETTINGS_TRANSACTIONS,
	SET_SETTINGS_TRANSACTIONS
} from '@/sections/transactions/shared/apisettings-mutation-types.js';

import { SET_INTEGRATIONS_OPTIONS } from '@/sections/options/options-mutation-type.js';

import { logEntrytransactionTypes } from '@/sections/transactions/shared/transactions-filters-params';

export default {
	name: 'GlobalTransactions',
	mixins: [CustomerOptions],
	components: {
		Button,
		Search,
		ResultCounter,
		GlobalTransactionsAdvancedFilters,
		ScrollContainer,
		NoResults,
		Modals,
		IconInfo,
		GlobalTransactionsList,
		Spinner,
		DropdownV2,
		DropdownMultiselectV2
	},
	props: {
		transactionId: {
			type: String
		},
		correlationId: {
			type: String
		},
		logEntrytransactionTypes: {
			type: String
		},
		search: {
			type: String
		},
		direction: {
			type: String
		},
		healths: {
			type: Array
		},
		apiTypes: {
			type: Array
		},
		customers: {
			type: Array
		},
		oemManufacturer: {
			type: String
		},
		oemMethod: {
			type: String
		},
		oemIntegrationName: {
			type: String
		},
		otherSource: {
			type: String
		},
		requestType: {
			type: String
		},
		isDMSIS: {
			type: String
		},
		minDatetime: {
			type: String
		},
		maxDatetime: {
			type: String
		},
		transactionMethod: {
			type: String
		},
		integrationCodes: {
			type: Array
		},
		primaryIntegrationVendorId: {
			type: Number
		},
		recordStart: {
			type: Number
		},
		maxCount: {
			type: Number
		},
		orderBy: {
			type: String
		},
		orderDirection: {
			type: String
		},
		position: {
			type: String,
			default: 'bottom'
		}
	},
	data() {
		return {
			filters: {},
			defaultFilters: {
				transactionId: undefined,
				correlationId: undefined,
				logEntrytransactionTypes: 'ThirdPartyVendor',
				search: undefined,
				direction: undefined,
				healths: [],
				customers: [],
				apiTypes: [],
				oemManufacturer: undefined,
				oemMethod: undefined,
				oemIntegrationName: undefined,
				otherSource: undefined,
				requestType: undefined,
				isDMSIS: undefined,
				minDatetime: formatDate(
					new Date(sub(new Date(), { weeks: 1 }))
				),
				maxDatetime: formatDate(new Date()),
				transactionMethod: 'SecureVue',
				primaryIntegrationVendorId: 0,
				integrationCodes: [],
				recordStart: 1,
				maxCount: 100,
				orderBy: 'timestamp',
				orderDirection: 'desc'
			},
			showAdvancedFiltersModal: false,
			transactionsColumns: {
				id: 'Id',
				company: 'Company',
				correlationId: 'Correlation Id',
				customerName: 'Customer Name',
				datetime: 'datetime',
				direction: 'Direction',
				logType: 'Log Type',
				method: 'Method',
				type: 'Type',
				integratorName: 'Vendor Name'
			},
			page: 1,
			loading: false,
			loadingMore: false,
			showModal: false,
			resetAllByDefault: false,
			exportingAllTransaction: false
		};
	},
	computed: {
		...mapState(MODULE_NAME, [GLOBAL_TRANSACTIONS, RELOAD_TRANSACTIONS]),
		...mapState('settingstransactions', [SETTINGS_TRANSACTIONS]),

		showRestoreFiltersButton() {
			return this.totalFiltersNumber > 0;
		},
		totalFiltersNumber() {
			return countsDiffObjects(
				{ ...this.defaultFilters },
				{ ...this.filters },
				['search', 'apiTypes'] //array of key to exclude on this comparation
			);
		},
		advancedFilterLabel() {
			if (this.totalFiltersNumber) {
				return `Advanced Filters (${this.totalFiltersNumber})`;
			}
			return `Advanced Filters`;
		},
		csvDataFileName() {
			let today = new Date();
			let strDay = today.toLocaleString();

			return `transaction_log_${strDay.split(', ')[0]}_${
				strDay.split(', ')[1]
			}.csv`;
		},
		healthOptions() {
			const healthOptions = _.get(
				this[SETTINGS_TRANSACTIONS],
				'health',
				[]
			);
			if (healthOptions.length > 0) {
				return healthOptions.map(item => ({
					value: item.value,
					text: item.text
				}));
			}
			return [];
		},
		transactionTypeOptions() {
			const transactions = _.get(
				this[SETTINGS_TRANSACTIONS],
				'transactionType',
				[]
			);
			if (transactions.length > 0) {
				return transactions.map(item => ({
					disabled: item.value !== 'ThirdPartyVendor',
					value: item.value,
					text: item.text
				}));
			}
			return [];
		},
		globalTransactionsList() {
			return this[GLOBAL_TRANSACTIONS]?.items || [];
		},
		hasResults() {
			return this.globalTransactionsList?.length && !this.loading;
		},
		hasMoreItemsToLoad() {
			return (
				this[GLOBAL_TRANSACTIONS].page <
				this[GLOBAL_TRANSACTIONS].totalPageCount
			);
		},
		resultCounter() {
			if (!_.isEmpty(this[GLOBAL_TRANSACTIONS].items)) {
				return `${this[GLOBAL_TRANSACTIONS].items.length} of ${this[GLOBAL_TRANSACTIONS].totalCount}`;
			}
			return '';
		},
		isDisabledExportButton() {
			return (
				_.isEmpty(this[GLOBAL_TRANSACTIONS].items) ||
				this.exportingAllTransaction
			);
		}
	},
	async created() {
		await this.getApiTransactionSettings();
		await this.fetchIntegrationsOpts();
	},
	methods: {
		...mapActions(MODULE_NAME, {
			getGlobalTransactions: SET_GLOBAL_TRANSACTIONS
		}),
		...mapActions('settingstransactions', {
			getTransactionSettings: SET_SETTINGS_TRANSACTIONS
		}),
		...mapActions('options', {
			setIntegrationsOpts: SET_INTEGRATIONS_OPTIONS
		}),
		async exportAll() {
			this.exportingAllTransaction = true;
			let result = {};
			let query = { ...this.filters };
			query.recordStart = 1;

			query.oemIntegrationName = query.requestType
				? query.requestType
				: query.oemIntegrationName;

			if (!this.primaryIntegrationVendorId) {
				delete query.primaryIntegrationVendorId;
			}

			query.minDatetime = parseDateToUTC(query.minDatetime);
			query.maxDatetime = parseDateToUTC(query.maxDatetime, true);

			query.maxCount = _.get(
				this[GLOBAL_TRANSACTIONS],
				'totalCount',
				500000
			);
			query.exportData = true;
			try {
				await this.getGlobalTransactions(query).then(response => {
					result = response;
				});
			} finally {
				this.exportingAllTransaction = false;
			}
			if (!_.isEmpty(result)) {
				downloadCSV(
					_.get(result, 'items', []),
					this.transactionsColumns,
					this.csvDataFileName
				);
			}
		},
		async getApiTransactionSettings() {
			await this.getTransactionSettings();
		},
		async fetchIntegrationsOpts() {
			await this.setIntegrationsOpts();
		},

		async fetchData(page) {
			if (!this.loadingMore) {
				this.loading = true;
			}

			let query = removeObjectEmptyValues(this.filters);

			query.recordStart = this.resetAllByDefault ? 1 : page;

			query.oemIntegrationName = query.requestType
				? query.requestType
				: query.oemIntegrationName;

			if (!this.primaryIntegrationVendorId) {
				delete query.primaryIntegrationVendorId;
			}
			query.minDatetime = parseDateToUTC(query.minDatetime);
			query.maxDatetime = parseDateToUTC(query.maxDatetime, true);

			try {
				await this.getGlobalTransactions(query);
			} finally {
				this.loading = false;
				this.loadingMore = false;
				if (this.resetAllByDefault) {
					this.resetAllByDefault = false;
				}
			}
		},
		handledAdvancedFilters() {
			this.showAdvancedFiltersModal = true;
			this.$refs.iconInfo.hidePopover();
			this.$refs.transactionTypeRef.closeMenu();
			this.$refs.customersRef.closeMenu();
			this.$refs.healthRef.closeMenu();
		},
		closeAdvanceFilters(value) {
			this.showAdvancedFiltersModal = value;
		},
		setFilters(filters) {
			this.filters.oemIntegrationName =
				filters.logEntrytransactionTypes ==
				logEntrytransactionTypes.VueDmsInternal
					? filters.requestType
					: filters.logEntrytransactionTypes ==
					  logEntrytransactionTypes.Oem
					? filters.oemIntegrationName
					: null;
			Object.assign(this.filters, filters);
		},
		resetFilters() {
			this.resetAllByDefault = true;
			this.filters = _.cloneDeep(this.defaultFilters);
		},
		cancel() {
			this.showModal = false;
			this.loading = false;
			this.handledAdvancedFilters();
		},
		async reloadPage() {
			this.showModal = false;
			await this.fetchData();
		},
		updateRoute(obj) {
			let queries = removePropertiesWithSameValues(
				obj,
				this.defaultFilters
			);
			queries = removeObjectEmptyValues(queries);
			this.$router
				.push({
					path: `/transactions`,
					query: queries
				})
				.catch(() => {});
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page += 1;
			}
		}
	},
	mounted() {
		document.addEventListener('click', this.handleClickOutside);
	},
	watch: {
		filters: {
			deep: true,
			handler(obj) {
				this.updateRoute(obj);
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(1);
				}
			}
		},
		page: {
			handler(page) {
				this.fetchData(page);
			}
		},
		'$route.query': {
			immediate: true,
			deep: true,
			handler(newQuery) {
				let newfilters = { ...this.defaultFilters, ...newQuery };
				if (
					Object.keys(newQuery).includes('customers') &&
					!Array.isArray(newQuery.customers)
				) {
					newfilters.customers = [newfilters.customers];
				}

				if (
					Object.keys(newQuery).includes('healths') &&
					!Array.isArray(newQuery.healths)
				) {
					newfilters.healths = [newfilters.healths];
				}

				if (
					Object.keys(newQuery).includes('integrationCodes') &&
					!Array.isArray(newQuery.integrationCodes)
				) {
					newfilters.integrationCodes = [newfilters.integrationCodes];
				}
				if (!_.isEqual(newfilters, this.filters)) {
					this.filters = _.cloneDeep(newfilters);
				}
			}
		},
		[RELOAD_TRANSACTIONS]: {
			deep: true,
			async handler(val) {
				if (val) {
					this.page = 1;
					await this.fetchData(1);
					this.$store.dispatch(
						'globaltransactions/reloadTransactions',
						false
					);
				}
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		to.meta.breadcrumb = [
			{
				name: 'Global Transaction Viewer',
				data: { path: from.fullPath }
			},
			{
				name: 'Details'
			}
		];
		const obj = { path: to.fullPath, breadcrumb: to.meta.breadcrumb };
		this.$store.commit('setBreadCrumbs', obj);
		next();
	}
};
</script>

<style lang="scss" scoped>
/**
  * Global Transaction Viewer Mananger
**/

.results-toolbar__icon {
	display: flex;
}

.results-toolbar__filter.results-toolbar--global-transaction {
	&__mobile {
		@include mq($from: lg) {
			display: none;
		}
	}

	&__desktop {
		@include mq($until: lg) {
			display: none;
		}
		.dropdown {
			width: 140px;
		}
	}
}

::v-deep .popover {
	z-index: 999;
}
</style>
